<template>
  <transition name="fading">
    <Loading v-if="isLoading" />
  </transition>

  <div class="admin-page wide">
    <div class="page-hero">
      <div>
        <p class="page-kicker">State Access Control</p>
        <h1 class="page-title">State Health Organizations</h1>
        <p class="page-subtitle">
          Configure which clinics in a state are watchable by a state-level monitoring organization.
        </p>
      </div>
      <button class="ui-button primary" @click="$router.push('/state-access/register')">
        <i class="fa-solid fa-plus"></i> Create state org
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <span class="stat-label">State orgs</span>
        <strong class="stat-value">{{ rawData.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Filtered</span>
        <strong class="stat-value">{{ renderData.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Watchable clinics</span>
        <strong class="stat-value">{{ totalWatchableClinics }}</strong>
      </div>
    </div>

    <div class="toolbar">
      <div class="toolbar-search">
        <SearchBar
          searchPlaceholder="Search state org, state, or contact..."
          buttonPlaceholder="Search"
          @OutputSearch="handleSearch"
        />
      </div>
    </div>

    <div class="table-card">
      <table class="ui-table mobile-card-table">
        <thead>
          <tr>
            <th class="text-start">State organization</th>
            <th class="text-start">State</th>
            <th class="text-start">Contact</th>
            <th class="text-start">Watchable clinics</th>
            <th class="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in paginatedData"
            :key="item.StateHealthOrgUuid"
            class="clickable-row"
            @click="goToEditor(item.StateHealthOrgUuid)"
          >
            <td data-label="State org">
              <span class="table-primary">{{ item.Name || 'Unnamed state org' }}</span>
              <span class="table-secondary">
                <span class="badge-soft" :class="item.isActive ? 'success' : 'muted'">
                  {{ item.isActive ? 'Active' : 'Inactive' }}
                </span>
              </span>
            </td>
            <td data-label="State">
              <span class="table-primary">{{ item.StateName || 'No state assigned' }}</span>
              <span class="table-secondary">{{ item.StateCode || 'String-based state' }}</span>
            </td>
            <td data-label="Contact">
              <span class="table-primary">{{ item.ContactEmail || 'No email' }}</span>
              <span class="table-secondary">{{ item.ContactPhone || 'No phone' }}</span>
            </td>
            <td data-label="Clinics">
              <span class="table-primary">{{ item.watchable_clinic_count || 0 }}</span>
              <span class="table-secondary">Clinics visible to this state org</span>
            </td>
            <td class="text-end" data-label="Actions">
              <button class="icon-button-soft" @click.stop="goToEditor(item.StateHealthOrgUuid)" title="Edit state org">
                <i class="fas fa-pen"></i>
              </button>
            </td>
          </tr>
          <tr v-if="paginatedData.length === 0">
            <td colspan="5">
              <div class="empty-state">
                <strong>No state health organizations found.</strong>
                <p>Create a state org to begin assigning watchable clinics.</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination-row" v-if="totalPages > 1">
        <span class="table-secondary">Page {{ currentPage }} of {{ totalPages }}</span>
        <div class="table-actions">
          <button class="icon-button-soft" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
            <ChevronLeft :size="16" />
          </button>
          <button class="icon-button-soft" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
            <ChevronRight :size="16" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/Loading.vue';
import SearchBar from '@/components/SearchBar.vue';
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { getStateHealthOrgs } from '@/services/stateHealthOrgServices';

export default {
  name: 'StateHealthOrgList',
  components: {
    Loading,
    SearchBar,
    ChevronLeft,
    ChevronRight,
  },
  data() {
    return {
      rawData: [],
      renderData: [],
      currentPage: 1,
      pageLimit: 10,
      isLoading: false,
    };
  },
  computed: {
    paginatedData() {
      const start = (this.currentPage - 1) * this.pageLimit;
      return this.renderData.slice(start, start + this.pageLimit);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.renderData.length / this.pageLimit));
    },
    totalWatchableClinics() {
      return this.rawData.reduce((total, item) => total + (item.watchable_clinic_count || 0), 0);
    },
  },
  created() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const result = await getStateHealthOrgs();
        this.rawData = [...(result.data || [])];
        this.renderData = [...this.rawData];
      } finally {
        this.isLoading = false;
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    handleSearch(searchQuery) {
      const query = (searchQuery || '').toLowerCase();
      this.currentPage = 1;
      this.renderData = this.rawData.filter((item) => [
        item.Name,
        item.StateName,
        item.StateCode,
        item.ContactEmail,
        item.ContactPhone,
      ].some((value) => String(value || '').toLowerCase().includes(query)));
    },
    goToEditor(uuid) {
      this.$router.push({ name: 'state-access-edit', params: { id: uuid } });
    },
  },
};
</script>

<style scoped>
.clickable-row {
  cursor: pointer;
}

.table-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
